import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { getUserProfile } from "../api/auth";
import { verifyHandoffSession } from "../api/client";
import { receiveAuthHandoff, trustedHandoffOrigin } from "../utils/authHandoff";
import {
  clearAuth,
  loadAuth,
  isAuthPersistent,
  profileToStoredAuth,
  saveAuth,
  type StoredAuth,
} from "../context/authStorage";
import { useAuth } from "../context/AuthContext";

const DEFAULT_TARGET = "/suggestions";

// Cross-app identity comes from a nonce-bound message from a trusted opener,
// then the backend verifies the token against the requested account/user.
export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeAuthHandoff } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const userId = searchParams.get("user_id");
    const accountId = searchParams.get("account_id");
    const requestedTarget = searchParams.get("target") ?? DEFAULT_TARGET;
    const target = requestedTarget.startsWith("/") && !requestedTarget.startsWith("//") && !requestedTarget.includes("\\") ? requestedTarget : DEFAULT_TARGET;
    const handoffId = searchParams.get("handoff_id");
    const sourceOrigin = trustedHandoffOrigin(searchParams.get("referrer_url"));

    if (!userId || !accountId || !handoffId || !sourceOrigin || !window.opener) {
      navigate("/login", { replace: true });
      return;
    }

    const previousAuth = loadAuth();
    const previousPersistent = isAuthPersistent();

    // The minimal state is only published after backend verification.
    const minimal: StoredAuth = {
      user: {
        id: userId,
        name: "",
        email: "",
        verified: false,
        stytch_user_id: "",
        account_id: accountId,
        session_jwt: null,
        role: "",
        status: "",
        role_id: null,
        stytch_org_id: null,
        user_info: null,
      },
      account: { id: accountId, name: "", stytch_org_id: "" },
    };

    let cancelled = false;
    (async () => {
      try {
        const token = await receiveAuthHandoff(handoffId, sourceOrigin);
        if (cancelled) return;
        await verifyHandoffSession(token, userId, accountId);
        if (cancelled) return;
        if (loadAuth()?.user.session_jwt !== previousAuth?.user.session_jwt) {
          throw new Error("The active session changed during sign-in handoff.");
        }
        minimal.user.session_jwt = token;
        saveAuth(minimal, true);
        const { user: profile } = await getUserProfile(userId);
        if (cancelled || loadAuth()?.user.session_jwt !== token) return;
        const fallbackAccountName = previousAuth?.account.name ?? "";
        const stored = profileToStoredAuth(profile, fallbackAccountName);
        completeAuthHandoff(stored);
        navigate(target, { replace: true });
      } catch (err) {
        if (cancelled) return;
        // Roll back the minimal auth so we don't leave a half-populated
        // session sitting in storage.
        if (loadAuth()?.user.session_jwt === minimal.user.session_jwt) {
          if (previousAuth) saveAuth(previousAuth, previousPersistent);
          else clearAuth();
        }
        if (process.env.NODE_ENV !== "production") {
          console.error("AuthCallback failed:", err);
        }
        setError(
          "Sign-in handoff failed. Please sign in to ESS directly.",
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, completeAuthHandoff]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        width: "100%",
        height: "100vh",
        bgcolor: "background.default",
        px: 2,
        textAlign: "center",
      }}
    >
      {error ? (
        <>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            color="primary"
          >
            Go to sign in
          </Button>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Signing you in…
          </Typography>
        </>
      )}
    </Box>
  );
}
